import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { AppProvider } from "@/context/AppContext";
import Layout from "@/components/Layout";
import { Protected } from "@/components/Protected";
import WorkspaceHome from "@/components/WorkspaceHome";
import Login from "@/pages/Login";
import AgentHome from "@/pages/AgentHome";
import ConnectedStack from "@/pages/ConnectedStack";
import Playbooks from "@/pages/Playbooks";
import Team from "@/pages/Team";
import Alloy from "@/pages/Alloy";
import AllBrands from "@/pages/AllBrands";
import AgentChatWorkspace from "@/pages/AgentChatWorkspace";
import AgentSessions from "@/pages/AgentSessions";
import AgentAddFiles from "@/pages/AgentAddFiles";
import AgentSkillsAutomations from "@/pages/AgentSkillsAutomations";
import Offers from "@/pages/Offers";
import Brain from "@/pages/Brain";
import DailyBrief from "@/pages/growth/DailyBrief";
import Goals from "@/pages/growth/Goals";
import Plan from "@/pages/growth/Plan";
import GrowthScore from "@/pages/growth/GrowthScore";
import Experiments from "@/pages/growth/Experiments";
import { SAVED_FEATURE_REDIRECTS, connectionRedirectTarget } from "@/dashboardRoutes";

function ConnectionsLegacyRedirect() {
  const { search } = useLocation();
  return <Navigate to={connectionRedirectTarget(search)} replace />;
}

const OffersFixture = process.env.NODE_ENV === "development" ? require("./dev/OffersFixture").default : null;

const AutomationsFixture = process.env.NODE_ENV === "development" ? require("./dev/AutomationsFixture").default : null;

const SkillsFixture = process.env.NODE_ENV === "development" ? require("./dev/SkillsFixture").default : null;
const UpdatesFixture = process.env.NODE_ENV === "development" ? require("./dev/UpdatesFixture").default : null;
const BrandsFixture = process.env.NODE_ENV === "development" ? require("./dev/BrandsFixture").default : null;
const ViewAsFixture = process.env.NODE_ENV === "development" ? require("./dev/ViewAsFixture").default : null;
const SharingFixture = process.env.NODE_ENV === "development" ? require("./dev/SharingFixture").default : null;

function AppRoutes() {
  return (
    <Routes>
      {process.env.NODE_ENV === "development" && UpdatesFixture && <Route path="/__dev/updates" element={<UpdatesFixture />} />}
      {process.env.NODE_ENV === "development" && SkillsFixture && <Route path="/__dev/skills" element={<SkillsFixture />} />}
      {process.env.NODE_ENV === "development" && AutomationsFixture && <Route path="/__dev/automations" element={<AutomationsFixture />} />}
      {process.env.NODE_ENV === "development" && OffersFixture && <Route path="/__dev/offers" element={<OffersFixture />} />}
      {process.env.NODE_ENV === "development" && BrandsFixture && <Route path="/__dev/brands" element={<BrandsFixture />} />}
      {process.env.NODE_ENV === "development" && ViewAsFixture && <Route path="/__dev/view-as" element={<ViewAsFixture />} />}
      {process.env.NODE_ENV === "development" && SharingFixture && <Route path="/__dev/sharing" element={<SharingFixture />} />}
      <Route path="/login" element={<Login />} />
      <Route
        path="/"
        element={
          <Protected>
            <Layout />
          </Protected>
        }
      >
        <Route index element={<WorkspaceHome><AgentHome /></WorkspaceHome>} />
        <Route path="brands" element={<AllBrands />} />
        <Route path="playbooks" element={<Playbooks />} />
        <Route path="connections" element={<ConnectedStack />} />
        <Route path="alloy" element={<Alloy />} />
        <Route path="team" element={<Team />} />
        <Route path="offers" element={<Offers />} />
        <Route path="brain" element={<Brain />} />
        {/* The Test section (growth system), pilot workspaces only; each page redirects everyone else to Your Agent. */}
        <Route path="test/brief" element={<DailyBrief />} />
        <Route path="test/goals" element={<Goals />} />
        <Route path="test/plan" element={<Plan />} />
        <Route path="test/score" element={<GrowthScore />} />
        <Route path="test/experiments" element={<Experiments />} />
        <Route path="agent/chat" element={<AgentChatWorkspace />} />
        <Route path="agent/sessions" element={<AgentSessions />} />
        <Route path="agent/files" element={<Navigate to="/brain?collection=files" replace />} />
        <Route path="agent/brain" element={<Navigate to="/brain" replace />} />
        <Route path="agent/add-files" element={<AgentAddFiles />} />
        <Route path="agent/automations" element={<AgentSkillsAutomations />} />
        <Route path="skills" element={<Navigate to="/agent/automations?tab=skills" replace />} />
        <Route path="settings/connections" element={<ConnectionsLegacyRedirect />} />
        <Route path="settings/team" element={<Navigate to="/team" replace />} />
        <Route path="stack" element={<Navigate to="/connections" replace />} />
        {SAVED_FEATURE_REDIRECTS.map((path) => (
          <Route key={path} path={path} element={<Navigate to="/" replace />} />
        ))}
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <div className="App">
      <AppProvider>
        <TooltipProvider delayDuration={200}>
          <BrowserRouter>
            <AppRoutes />
          </BrowserRouter>
          <Toaster
            position="bottom-right"
            theme="light"
            toastOptions={{
              style: { background: "#fff", border: "1px solid rgba(15,36,66,0.12)", color: "#0F2442" },
            }}
          />
        </TooltipProvider>
      </AppProvider>
    </div>
  );
}

export default App;
